import { SupportAgentTicket } from "@src/types/support";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

export interface TicketFilterTab<TFilter extends string = string> {
  /** Display label */
  label: string;
  /** The filter key emitted to the parent via onFilter */
  filterKey: TFilter;
  /**
   * Optional function to derive a badge count from the full tickets array.
   * Return undefined / 0 to hide the badge.
   */
  countFn?: (tickets: SupportAgentTicket[]) => number;
}

interface UseTicketFilterNavigationOptions<TFilter extends string> {
  tabs: TicketFilterTab<TFilter>[];
  activeFilter: TFilter;
  onFilter: (filter: TFilter) => void;
  tickets: SupportAgentTicket[];
}

export const useTicketFilterNavigation = <TFilter extends string>({
  tabs,
  activeFilter,
  onFilter,
  tickets,
}: UseTicketFilterNavigationOptions<TFilter>) => {
  const getIndexForFilter = (filter: TFilter) => {
    const idx = tabs.findIndex((t) => t.filterKey === filter);
    return idx === -1 ? 0 : idx;
  };

  const [selectedIndex, setSelectedIndex] = useState(() =>
    getIndexForFilter(activeFilter),
  );

  useEffect(() => {
    setSelectedIndex(getIndexForFilter(activeFilter));
  }, [activeFilter]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [backgroundStyle, setBackgroundStyle] = useState({ width: 0, left: 0 });
  const [isFirstRender, setIsFirstRender] = useState(true);

  // Natural content widths measured before flex kicks in, used as flex-grow
  // weights so each tab claims space proportional to its content.
  const [flexGrowValues, setFlexGrowValues] = useState<number[]>([]);

  useLayoutEffect(() => {
    const buttons = buttonRefs.current.filter(Boolean) as HTMLButtonElement[];
    if (buttons.length === 0) return;

    // Temporarily strip flex so scrollWidth reflects true content size
    buttons.forEach((btn) => {
      btn.style.flex = "none";
    });

    const naturalWidths = buttons.map((btn) => btn.scrollWidth);
    const total = naturalWidths.reduce((sum, w) => sum + w, 0);

    // Restore flex — actual values applied via the returned array
    buttons.forEach((btn) => {
      btn.style.flex = "";
    });

    // Normalise to values that sum to the number of tabs (keeps flex-grow
    // magnitudes reasonable while preserving the ratio)
    const normalized = naturalWidths.map(
      (w) => (w / total) * naturalWidths.length,
    );
    setFlexGrowValues(normalized);
  }, []); // intentionally empty — tab labels don't change at runtime

  useLayoutEffect(() => {
    const btn = buttonRefs.current[selectedIndex];
    if (btn) {
      setBackgroundStyle({ width: btn.offsetWidth, left: btn.offsetLeft });
    }
  }, [selectedIndex, tabs, isFirstRender, flexGrowValues]);

  useEffect(() => {
    if (isFirstRender) setIsFirstRender(false);
  }, [isFirstRender]);

  const slidingTransition = isFirstRender
    ? "none"
    : "left 0.3s ease, width 0.3s ease";

  const badgeCounts = tabs.map((tab) =>
    tab.countFn ? tab.countFn(tickets) : undefined,
  );

  const handleClick = (index: number, filterKey: TFilter) => {
    setSelectedIndex(index);
    onFilter(filterKey);
  };

  return {
    containerRef,
    buttonRefs,
    backgroundStyle,
    slidingTransition,
    selectedIndex,
    badgeCounts,
    flexGrowValues,
    handleClick,
  };
};
